/**
 * Source mode: the raw text of a structured shared document, in Monaco.
 *
 * The desktop app offers this as an escape hatch on every structured editor,
 * and `registerBuiltinCustomEditors.tsx` says why for `.canvas` — "a free and
 * genuinely useful escape hatch when a board will not open". Until now the
 * console's only answer to a document it could not render was the desktop
 * handoff panel.
 *
 * ---
 *
 * **This is a projection, not a second binding, and the difference is the whole
 * design.**
 *
 * A structured Y.Doc holds structure. There is no text field in a canvas board
 * or a spreadsheet for an editor to bind to, so the only honest text is
 * `codec.exportToFile(yDoc)` and the only honest way back is
 * `codec.applyFromFile(yDoc, text)`. That pair is already the codec contract —
 * `applyFromFile` must be safe on a populated Y.Doc and must consume its own
 * `exportToFile` output unchanged — and it is already what an AI edit does to
 * these documents when a codec implements no structured patch.
 *
 * Two consequences the reader has to be told rather than discover:
 *
 *  - **Apply rewrites the whole document.** It is a single-writer escape hatch,
 *    not a co-editing surface. A remote change while source mode is open
 *    re-projects silently when the draft is untouched, and says so when it is
 *    not, because applying over it would discard the other writer's work.
 *  - **Nothing is written per keystroke.** The draft leaves this component on
 *    an explicit Apply, or when source mode closes with unapplied edits —
 *    matching the desktop, which saves before it switches away.
 *
 * The strictly better path exists for the subset of types whose codec is a
 * single `Y.Text`: bind Monaco to that field and get real collaborative source
 * editing with no projection at all. It is not built here because no editor the
 * console pins today has a text-shaped codec. When one does, the signal is a
 * `serializableDescriptor` of `kind: 'text'` carrying the `textField` to bind.
 */

import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import type { EditorHost } from '@nimbalyst/collab-bundle/editor';
import { BUTTON_GHOST, BUTTON_PRIMARY } from '../controlStyles';
import { EDITOR_LOADING } from './editorSurfaceStyles';
import type { BrowserCollabCodec } from '@nimbalyst/web-editors/registry';

/**
 * The same chunk the `code` document type loads, so opening source mode after a
 * code document (or the other way round) fetches nothing twice. Lazy for the
 * reason the whole Monaco pin is lazy: it is the largest thing this console can
 * download, and a session that never opens source mode must not pay for it.
 */
const MonacoSourceView = lazy(async () => {
  const module = await import('../extensions/webMonacoBundle');
  return { default: module.MonacoSourceView };
});

/**
 * Marks this component's own writes so the document listener can ignore them.
 *
 * `applyFromFile` runs its own `yDoc.transact`, and a nested transaction adopts
 * the outer one's origin — so wrapping the call is enough to label it. Without
 * the label a codec that normalizes on the way in (canvas reorders keys) looks
 * exactly like a peer edit, and the reader would be warned about a conflict
 * with themselves every time they pressed Apply.
 */
const SOURCE_MODE_ORIGIN = 'web-console:source-mode';

/** A projection is text. Bytes are decoded rather than refused. */
function asText(source: string | Uint8Array): string {
  if (typeof source === 'string') return source;
  return new TextDecoder('utf-8').decode(source);
}

/**
 * A codec is allowed to refuse a document it cannot serialize, and the real
 * canvas codec did exactly that on both shared boards until NIM-5378 -- each
 * held a sticky the author had not typed into yet.
 *
 * That refusal has to be survivable here specifically, because source mode is
 * the fallback of last resort: a board that will not open is the reason to
 * reach for it. Letting `exportToFile` throw out of a render takes the page
 * down and leaves no way back to the editor.
 *
 * Failure is a value rather than an empty string. An empty editor over a
 * document that is not empty is the dangerous answer: the reader edits it,
 * applies, and the codec faithfully writes the emptiness back.
 */
type Projection = { ok: true; text: string } | { ok: false; message: string };

function projectSafely(codec: BrowserCollabCodec, yDoc: unknown): Projection {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return { ok: true, text: asText(codec.exportToFile(yDoc as any)) };
  } catch (cause) {
    return {
      ok: false,
      message: cause instanceof Error ? cause.message : String(cause),
    };
  }
}

export interface ExtensionSourceModeProps {
  host: EditorHost;
  codec: BrowserCollabCodec;
  /** Seam for tests; the app always uses the lazy Monaco chunk. */
  renderEditor?: typeof MonacoSourceView;
}

export function ExtensionSourceMode({ host, codec, renderEditor }: ExtensionSourceModeProps) {
  const yDoc = host.collaboration?.yDoc;
  const write = useCallback((text: string): Projection => {
    if (!yDoc) return { ok: true, text };
    yDoc.transact(() => codec.applyFromFile(yDoc, text), SOURCE_MODE_ORIGIN);
    // Re-project rather than trusting the draft: a codec is allowed to
    // normalize, and showing the text the document actually holds is the only
    // way the reader can tell an applied edit from an unapplied one.
    return projectSafely(codec, yDoc);
  }, [codec, yDoc]);

  const [projection, setProjection] = useState<Projection>(() =>
    yDoc ? projectSafely(codec, yDoc) : { ok: true, text: '' },
  );
  /** The text the document actually holds, or null when it could not be read. */
  const projected = projection.ok ? projection.text : null;
  const [draft, setDraft] = useState(projected ?? '');
  /** A peer changed the document while this draft was being edited. */
  const [staleUnder, setStaleUnder] = useState(false);
  const readOnly = host.readOnly;
  const dirty = projected !== null && draft !== projected;

  /**
   * The latest draft, for the unmount write-back below.
   *
   * Source mode closes by unmounting this component — from the toolbar here,
   * from the page's Document actions menu, or by leaving the document — and all
   * three have to write an unapplied edit rather than drop it. A cleanup
   * closure captures the draft it was created with, so the newest one is read
   * through a ref instead.
   */
  const pending = useRef({ draft, dirty, readOnly, projected });
  pending.current = { draft, dirty, readOnly, projected };

  /**
   * Track the document underneath the draft.
   *
   * An untouched draft simply follows the room, which is what makes source mode
   * usable as a live read of a document other people are editing. A touched one
   * cannot: re-projecting would throw the reader's edit away, and applying over
   * the peer would throw theirs away. Both are the reader's call, so it says so
   * and leaves the choice on the toolbar.
   */
  useEffect(() => {
    if (!yDoc) return;
    const onUpdate = (_update: Uint8Array, origin: unknown) => {
      if (origin === SOURCE_MODE_ORIGIN) return;
      const next = projectSafely(codec, yDoc);
      setProjection(next);
      // A peer edit that leaves the document unserializable has nothing to
      // re-project onto. The refusal below replaces the editor; the draft is
      // left where it is so it is still there if the document recovers.
      if (!next.ok) return;
      setDraft((current) => {
        if (current === next.text) return current;
        // Read through the ref: this listener is installed once per document.
        if (pending.current.dirty) {
          setStaleUnder(true);
          return current;
        }
        return next.text;
      });
    };
    yDoc.on('update', onUpdate);
    return () => {
      yDoc.off('update', onUpdate);
    };
  }, [codec, yDoc]);

  /**
   * Write an unapplied edit on the way out, the way the desktop saves before it
   * switches away from source mode. Deliberately not gated on how source mode
   * was closed: dropping someone's typing because they used the menu instead of
   * the button would be data loss with a UI explanation.
   */
  useEffect(() => () => {
    const last = pending.current;
    // `projected === null` means this view never held the document's text.
    // Writing the draft back then would apply an edit to something nobody
    // read -- and for the canvas case that is a whole board replaced by
    // whatever the empty editor contained.
    if (!yDoc || last.projected === null || !last.dirty || last.readOnly) return;
    yDoc.transact(() => codec.applyFromFile(yDoc, last.draft), SOURCE_MODE_ORIGIN);
  }, [codec, yDoc]);

  /**
   * An unapplied draft is a real unsaved change, so the session should treat it
   * as one and flush on teardown. The clean direction is never reported from
   * here: the collaborative binding underneath owns that answer, and clearing
   * it would tell the session an edit it is still holding has been dealt with.
   */
  useEffect(() => {
    if (dirty) host.setDirty(true);
  }, [dirty, host]);

  const applyDraft = useCallback(() => {
    const applied = write(draft);
    setProjection(applied);
    if (applied.ok) setDraft(applied.text);
    setStaleUnder(false);
  }, [draft, write]);

  const Editor = renderEditor ?? MonacoSourceView;

  if (!yDoc) {
    return (
      <div className="collab-source-mode-unavailable" role="alert">
        This document has no collaborative session to read source from.
      </div>
    );
  }

  const unprojectable = !projection.ok;

  return (
    <div
      className="collab-source-mode flex min-h-0 flex-1 flex-col"
      data-source-mode={unprojectable ? 'unprojectable' : 'active'}
    >
      <div className="collab-source-mode-toolbar flex items-center gap-2 border-b border-nim bg-nim-secondary px-3 py-1.5">
        <span className="mr-auto text-[0.78rem] text-nim-muted">
          {unprojectable
            ? 'Source · unavailable'
            : readOnly
              ? 'Source · read-only'
              : dirty
                ? 'Source · not applied'
                : 'Source'}
        </span>
        {staleUnder && (
          <span
            className="collab-source-mode-stale text-[0.72rem] text-[var(--nim-error)]"
            role="status"
          >
            Someone else changed this document. Applying replaces their version.
          </span>
        )}
        {!readOnly && !unprojectable && (
          <button type="button" className={BUTTON_PRIMARY} disabled={!dirty} onClick={applyDraft}>
            Apply
          </button>
        )}
        {/* Always present. A refusal that stranded the reader in source mode
            would be worse than the failure it is reporting. */}
        <button
          type="button"
          className={BUTTON_GHOST}
          onClick={() => host.toggleSourceMode?.()}
        >
          Editor
        </button>
      </div>
      {!projection.ok ? (
        <div
          className="collab-source-mode-unprojectable flex min-h-0 flex-1 select-text flex-col gap-2 p-4"
          role="alert"
        >
          <div className="text-[0.85rem] font-medium text-nim">
            This document&rsquo;s source could not be read.
          </div>
          <div className="text-[0.78rem] leading-snug text-nim-muted">
            Its editor refused to convert it to text, so there is nothing to show here
            and nothing that could safely be written back.
          </div>
          <div className="font-mono text-[0.72rem] leading-snug text-nim-faint">
            {projection.message}
          </div>
        </div>
      ) : (
        <div className="collab-source-mode-editor min-h-0 flex-1">
          <Suspense fallback={<div className={EDITOR_LOADING}>Loading the source editor…</div>}>
            <Editor
              value={draft}
              fileName={host.fileName}
              theme={host.theme}
              readOnly={readOnly}
              onChange={setDraft}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}
